import React, { useEffect, useState } from 'react';

import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useMediaQuery, useTheme } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import { DateTimePicker } from '@material-ui/pickers';

import { SurveyRecipientSelector } from './survey-recipient-selector';
import { updateOrg } from '../../action/org/update-org';
import { loadEmployeeGroups } from '../../employee-groups/actions/load-employee-groups';
import {
  employeesForGroupIdSelector,
  employeesForOrgIdSelector,
  employeesStateForOrgIdAndStatusSelector,
} from '../../reducers/employee-selectors';
import { surveyForIdSelector } from '../../reducers/survey-selectors';
import { EMPLOYEE_STATUS, loadOrgEmployeesByStatus } from '../actions/load-org-employees-by-status';
import { createSurvey } from '../actions/survey/create-survey';
import { updateSurvey } from '../actions/survey/update-survey';

import { EmployeeMessageTemplates } from '../../employee-messages/message-composer/employee-message-templates';
import { FeatureFlagged, FeatureGatedVariant } from '../../feature-gates/feature-flagged';
import { FeatureGates } from '../../feature-gates/feature-gates';
import { OnMessageSendInput } from '../../typescript-types/messages/on-message-send-type';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';
import { stateCityOptionalStepProvider } from '../city-state-provider';

import { DEFAULT_SURVEY_EXPLANATION_MESSAGES } from './utils/default-survey-explanation-messages';
import { DEFAULT_SURVEY_REMINDER_MESSAGES } from './utils/default-survey-reminder-messages';
import { SURVEY_TYPE_DISPLAY } from './utils/survey-type-display';
import { allEmployeesKey } from '../../util/all-employees-option';
import { LanguageCode } from '../../util/languages';
import { MESSAGE_TYPE } from '../../util/message-types';
import { SURVEY_TYPES } from '../../util/survey-types';
import { TEMPLATE_TYPE } from '../../util/template-type';
import Message from '../../widgets/legacy-message';
import { LegacyMultiLanguageMessageComposer } from '../../widgets/message-composer/legacy-multiple-language-message-composer';
import { MultiLanguageMessageComposer } from '../../widgets/message-composer/multiple-language-message-composer';
import { collectMessageToLanguage } from '../../widgets/message-composer/wrappers/multiple-message-composer-dialog';
import { employeeLanguages } from '../utils/employee-languages';

const mapStateToProps = (state: any, { surveyId, orgId }: InputProps) => ({
  survey: surveyId ? surveyForIdSelector(state, surveyId) : null,
  employeesForGroupId: (groupId: number) => employeesForGroupIdSelector(state, groupId),
  employeesForOrg: employeesForOrgIdSelector(state, orgId),
  employeesStateForOrgIdAndStatus: employeesStateForOrgIdAndStatusSelector(
    state,
    orgId,
    EMPLOYEE_STATUS.CURRENT,
  ),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId, surveyId }: InputProps,
) => ({
  actions: {
    createSurvey: (survey: CreateSurveyRequest) => dispatch(createSurvey(orgId, survey)),
    updateSurvey: (survey: UpdateSurveyRequest) =>
      surveyId ? dispatch(updateSurvey(surveyId, survey)) : null,
    updateOrganization: (update: Partial<Org>) => dispatch(updateOrg(orgId, update)),
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(orgId)),
    loadOrgEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
  },
});

type InputProps = {
  orgId: number;
  surveyId?: number;
  isAddingEmployee?: boolean;
  createWithNoRecipients?: boolean;
  surveyType: SurveyType;

  onCreate?: (surveyId: number) => void;
  onClose: () => void;
};
type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

const defaultMessageForSurveyType = {
  [SURVEY_TYPES.STANDARD]: '',
  [SURVEY_TYPES.ONBOARDING]:
    "Hi {{{employee.casualName}}}! This is {{{user.firstName}}} at {{{employee.companyName}}}. We're excited to have you and hope you're enjoying it here! We'd love your feedback. What is one thing we can improve about our training and onboarding process?",
  [SURVEY_TYPES.EXIT]:
    'Hi {{{employee.casualName}}} - This is {{{user.firstName}}} at {{{employee.companyName}}}. So that we can improve, what is the primary reason you are leaving? Please feel free to include any other feedback you have as well. Thank you.',
};

export const CreateSurveyDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  stateCityOptionalStepProvider(
    (o) => o.surveyPhoneNumber,
    ({
      actions,
      onClose,
      orgId,
      surveyId,
      survey,
      surveyType,
      employeesForGroupId,
      employeesForOrg,
      isAddingEmployee,
      createWithNoRecipients,
      onCreate,
      employeesStateForOrgIdAndStatus,
    }: Props) => {
      const [employeeGroup, setEmployeeGroup] = useState<EmployeeGroupWithAllOption | null>(null);
      const [employees, setEmployees] = useState<Array<Employee> | null>(null);
      const [endAt, setEndAt] = useState<Moment | null>(moment().add(2, 'days'));
      const [isSubscribed, setIsSubscribed] = useState<boolean>(
        Boolean(survey?.senderSubscribedToNotificationsAt ?? false),
      );
      const [isAnonymous, setIsAnonymous] = useState<boolean>(
        Boolean(survey?.isAnonymous ?? false),
      );
      const [sendAutomatedReminders, setSendAutomatedReminders] = useState<boolean>(
        Boolean(survey?.sendAutomatedReminders ?? true),
      );

      const theme = useTheme();
      const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

      let employeeRecipients: Employee[] = [];
      if (employees) {
        // sending to individual employees
        employeeRecipients = employees;
      } else if (employeeGroup && employeeGroup.employeeGroupId !== allEmployeesKey(orgId)) {
        // sending to an employee group
        employeeRecipients = employeesForGroupId(Number(employeeGroup.employeeGroupId));
      } else if (
        (!employeeGroup && !employees) ||
        (employeeGroup && employeeGroup.employeeGroupId === allEmployeesKey(orgId))
      ) {
        // no group or individual employees selected, sending to the "All Employees" group
        employeeRecipients = employeesForOrg;
      }
      employeeRecipients = employeeRecipients.filter((e) => e.isCurrentEmployee);

      const { languageCounts, defaultLanguage, languagesToSupport } =
        employeeLanguages(employeeRecipients);
      const initialLanguageToMessage: Record<LanguageCode, string> = {
        en: survey?.messageBody ?? defaultMessageForSurveyType[surveyType],
        ...(survey?.spanishMessageBody ? { es: survey.spanishMessageBody } : {}),
      };
      languagesToSupport.forEach((language) => {
        initialLanguageToMessage[language] = initialLanguageToMessage[language] || '';
      });

      const [languageToMessage, setLanguageToMessage] =
        useState<Record<LanguageCode, string>>(initialLanguageToMessage);
      const [reminderMessageBody, setReminderMessageBody] = useState<string>(
        survey?.reminderMessageBody ?? DEFAULT_SURVEY_REMINDER_MESSAGES.NON_ANONYMOUS.en,
      );
      const [reminderSpanishMessageBody, setReminderSpanishMessageBody] = useState<string>(
        survey?.reminderSpanishMessageBody ?? DEFAULT_SURVEY_REMINDER_MESSAGES.NON_ANONYMOUS.es,
      );

      const [viewDisclaimerMessage, setViewDisclaimerMessage] = useState<boolean>(false);
      const [canPersonalize, setCanPersonalize] = useState<boolean>(true);

      useEffect(() => {
        actions.loadOrgEmployeeGroups();
        actions.loadOrgEmployees();
      });

      if (!employeesStateForOrgIdAndStatus || !employeesStateForOrgIdAndStatus.success) {
        return null;
      }

      const endAtIsLessThanADayAway = moment.utc(endAt).isBefore(moment.utc().add(1, 'day'));

      const addDisclaimerMessage = (message: string, language: LanguageCode) => {
        if (language !== 'en' && language !== 'es') {
          return message;
        }
        if (isAnonymous) {
          return DEFAULT_SURVEY_EXPLANATION_MESSAGES.ANONYMOUS[language] + message;
        }
        return message + DEFAULT_SURVEY_EXPLANATION_MESSAGES.NON_ANONYMOUS[language];
      };

      const handleSendMessage = async (onMessageSendInput: OnMessageSendInput) => {
        const { languageToMessage: languageToMessageToSend } = onMessageSendInput;
        if (!endAt) {
          return { errorMessage: 'You must specify when the survey period ends' };
        }
        const newSurvey: CreateSurveyRequest = {
          messageBody: '',
          messageType: MESSAGE_TYPE.TEXT,
          employeeGroupIds:
            employeeGroup && employeeGroup.employeeGroupId !== allEmployeesKey(orgId)
              ? [String(employeeGroup.employeeGroupId)]
              : null,
          employeeIds: employees ? employees.map((e) => String(e.employeeId)) : null,
          orgId,
          endAt: createWithNoRecipients ? moment.utc().toDate() : endAt,
          senderSubscribedToNotificationsAt: isSubscribed ? new Date().toISOString() : undefined,
          surveyId,
          surveyType,
          sendAutomatedReminders: endAtIsLessThanADayAway ? false : sendAutomatedReminders,
          reminderMessageBody,
          reminderSpanishMessageBody,
          wasCreatedWithNoRecipients: createWithNoRecipients,
          isAnonymous,
        };
        const languages = Object.keys(languageToMessageToSend).filter((lc) =>
          languageToMessageToSend[lc].trim(),
        );
        if (languages.length === 1) {
          const [language] = languages;
          newSurvey.messageBody = addDisclaimerMessage(languageToMessageToSend[language], language);
          // Surveys only work with Spanish and english right now with
          // how the data is structured
        } else {
          newSurvey.messageBody = addDisclaimerMessage(languageToMessageToSend.en, 'en');
          // we don't want to add blank messages because it would result in an
          // empty survey being sent
          if (languageToMessageToSend.es && languageToMessageToSend.es.trim()) {
            newSurvey.spanishMessageBody = addDisclaimerMessage(languageToMessageToSend.es, 'es');
          }
        }
        if (surveyId) {
          return actions
            .updateSurvey({
              messageBody: newSurvey.messageBody,
              spanishMessageBody: newSurvey.spanishMessageBody,
              senderSubscribedToNotificationsAt: isSubscribed
                ? new Date().toISOString()
                : undefined,
            })
            ?.then((resp) => {
              onClose();
              return resp;
            });
        }
        return actions.createSurvey(newSurvey).then((newlyCreatedSurvey) => {
          if (onCreate) {
            onCreate(newlyCreatedSurvey.surveyId);
          }
          onClose();
          return newlyCreatedSurvey;
        });
      };

      const handleResendMessage = () => {
        // For the moment, we are only allowing you to resend to employees
        if (!surveyId || !employees || employees.length === 0) {
          return;
        }
        actions.updateSurvey({
          employeeIds: employees.map((e) => String(e.employeeId)),
          endAt,
          senderSubscribedToNotificationsAt: isSubscribed ? new Date().toISOString() : undefined,
        });
        onClose();
      };

      const handleError = (response: any) => {
        if (response.shouldClearOrgLocation) {
          actions.updateOrganization({
            city: null,
            state: null,
          });
        }
      };

      const handleSubscribedCheckbox = (
        _event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean,
      ) => setIsSubscribed(checked);
      const handleSendAutomatedRemindersCheckbox = (
        _event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean,
      ) => setSendAutomatedReminders(checked);

      const handleAnonymousCheckbox = (
        _event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean,
      ) => {
        if (checked) {
          setReminderMessageBody(DEFAULT_SURVEY_REMINDER_MESSAGES.ANONYMOUS.en);
          setReminderSpanishMessageBody(DEFAULT_SURVEY_REMINDER_MESSAGES.ANONYMOUS.es);
        } else {
          setReminderMessageBody(DEFAULT_SURVEY_REMINDER_MESSAGES.NON_ANONYMOUS.en);
          setReminderSpanishMessageBody(DEFAULT_SURVEY_REMINDER_MESSAGES.NON_ANONYMOUS.es);
        }
        setCanPersonalize(!checked);
        setIsAnonymous(checked);
      };
      const handleTemplateChange = (newTemplate: string) =>
        setLanguageToMessage({
          ...languageToMessage,
          en: newTemplate,
        });
      const handleEmployeeGroupSelect = (newEmployeeGroup: EmployeeGroupWithAllOption | null) => {
        setEmployeeGroup(newEmployeeGroup);
      };
      const handleEmployeeSelect = (newEmployees: Array<Employee> | null) => {
        setEmployees(newEmployees);
      };

      const handleEditReminderMessage = async () => {
        const reminderLanguageToMessage: Record<LanguageCode, string> = {
          en: reminderMessageBody,
          es: reminderSpanishMessageBody,
        };

        const returnVal = await collectMessageToLanguage(
          'Edit Reminder Message',
          'Edit the reminder message that will be sent one day before the survey ends',
          {
            enableText: true,
            enableEmail: false,
            languageToMessage: reminderLanguageToMessage,
            languagesToSupport,
            buttonClass: 'u-float-right',
            buttonText: 'Save Reminder Message',
          },
        );

        if (returnVal) {
          setReminderMessageBody(returnVal.languageToMessage.en);
          setReminderSpanishMessageBody(returnVal.languageToMessage.es);
        }
      };

      const handleViewDisclaimerMessage = () => setViewDisclaimerMessage(!viewDisclaimerMessage);
      const disclaimerMessage = isAnonymous
        ? DEFAULT_SURVEY_EXPLANATION_MESSAGES.ANONYMOUS
        : DEFAULT_SURVEY_EXPLANATION_MESSAGES.NON_ANONYMOUS;
      const disclaimerDialog = (
        <UiDialog
          onClose={handleViewDisclaimerMessage}
          size="xs"
          title="Survey Explanation Message"
        >
          <UiDialog.Content>
            <div className="typo-title u-margin-bottom-md">
              Employees will receive the following message alongside your survey question
            </div>
            <div className="typo-subtitle">English:</div>
            <Message canBeDismissed={false}>{disclaimerMessage.en}</Message>
            {languagesToSupport.includes('es') && (
              <>
                <div className="typo-subtitle u-margin-top">Spanish:</div>
                <Message canBeDismissed={false}>{disclaimerMessage.es}</Message>
              </>
            )}
          </UiDialog.Content>
        </UiDialog>
      );

      const title = `${isAddingEmployee ? 'Add Employee to' : surveyId ? 'Edit' : ''} ${
        SURVEY_TYPE_DISPLAY[surveyType]
      }`;
      let buttonText = 'Send Survey';
      if (surveyId) {
        buttonText = 'Edit Survey';
      } else if (createWithNoRecipients || surveyType !== SURVEY_TYPES.STANDARD) {
        buttonText = 'Create Survey';
      }

      return (
        <UiDialog size="sm" onClose={onClose} title={title}>
          <UiDialog.Content>
            <div>
              {(isAddingEmployee || surveyType === SURVEY_TYPES.STANDARD) &&
              !createWithNoRecipients ? (
                <SurveyRecipientSelector
                  orgId={orgId}
                  selectedEmployees={employees}
                  selectedEmployeeGroup={employeeGroup}
                  onEmployeeGroupSelect={handleEmployeeGroupSelect}
                  onEmployeeSelect={handleEmployeeSelect}
                  surveyId={surveyId}
                  surveyType={surveyType}
                />
              ) : null}
              {surveyType === SURVEY_TYPES.STANDARD && !createWithNoRecipients ? (
                <DateTimePicker
                  className="u-width-100 u-margin-bottom-md"
                  label="End Survey On"
                  value={endAt}
                  onChange={setEndAt}
                  disablePast
                  strictCompareDates
                  minDateMessage="Please select a time in the future"
                />
              ) : null}
              {!isAddingEmployee && (
                <div className="u-margin-top-sm u-margin-bottom-sm u-display-flex u-alignItems-center">
                  <Checkbox
                    className="u-no-padding"
                    checked={isAnonymous}
                    onChange={handleAnonymousCheckbox}
                  />
                  <div className="typo-title u-margin-left-sm">Receive anonymous responses</div>
                </div>
              )}
              <div className="u-margin-top-sm u-margin-bottom-sm u-display-flex u-alignItems-center">
                <Checkbox
                  className="u-no-padding"
                  checked={isSubscribed}
                  onChange={handleSubscribedCheckbox}
                />
                <div className="typo-title u-margin-left-sm">
                  Receive email notifications for responses
                </div>
              </div>
              {surveyType === SURVEY_TYPES.STANDARD ? (
                <div className="u-margin-top-sm u-margin-bottom-sm u-display-flex u-alignItems-center">
                  <Checkbox
                    className="u-no-padding"
                    checked={endAtIsLessThanADayAway ? false : sendAutomatedReminders}
                    disabled={endAtIsLessThanADayAway}
                    onChange={handleSendAutomatedRemindersCheckbox}
                  />
                  <div className="typo-title u-margin-left-sm">
                    Send an automated reminder one day before the survey ends
                  </div>
                  <div
                    role="presentation"
                    onClick={handleEditReminderMessage}
                    className="typo-subtitle typo-link u-margin-left-md"
                  >
                    Edit Message
                  </div>
                </div>
              ) : null}
              <div className="typo-subtitle typo-italic u-margin-y">
                {isAnonymous
                  ? DEFAULT_SURVEY_EXPLANATION_MESSAGES.ANONYMOUS_UI
                  : DEFAULT_SURVEY_EXPLANATION_MESSAGES.NON_ANONYMOUS_UI}
                <span
                  role="presentation"
                  onClick={handleViewDisclaimerMessage}
                  className="typo-subtitle typo-italic typo-link u-margin-left-sm"
                >
                  View Message
                </span>
                {viewDisclaimerMessage && disclaimerDialog}
              </div>
              {isAddingEmployee ? (
                <UiButton className="u-float-right" onClick={handleResendMessage}>
                  Send Survey
                </UiButton>
              ) : (
                <>
                  <FeatureFlagged
                    variant={FeatureGatedVariant.HIDE_ON_FLAG}
                    flagName={FeatureGates.INTEGRATED_MESSAGING}
                  >
                    <LegacyMultiLanguageMessageComposer
                      languageToMessage={languageToMessage}
                      languageCounts={languageCounts}
                      languagesToSupport={languagesToSupport}
                      defaultLanguage={defaultLanguage}
                      enableEmail={false}
                      enableText
                      messagePreviewEmployees={employeeRecipients}
                      messagePreviewType="survey"
                      disabled={moment(endAt).isBefore(moment())}
                      buttonText={buttonText}
                      buttonClass={surveyId ? 'u-float-right' : undefined}
                      onSendMessage={handleSendMessage}
                      canPersonalize={canPersonalize}
                      // because we don't let you personalize when the survey is anonymous,
                      // we want to override the check to display the preview to show our added text
                      alwaysDisplayPreview={isAnonymous}
                      customizePreviewMessage={addDisclaimerMessage}
                      onChange={setLanguageToMessage}
                      onError={handleError}
                      canTranslate
                    />
                  </FeatureFlagged>
                  <FeatureFlagged
                    variant={FeatureGatedVariant.SHOW_ON_FLAG}
                    flagName={FeatureGates.INTEGRATED_MESSAGING}
                  >
                    <MultiLanguageMessageComposer
                      languageToMessage={languageToMessage}
                      languageCounts={languageCounts}
                      languagesToSupport={languagesToSupport}
                      defaultLanguage={defaultLanguage}
                      enableEmail={false}
                      enableText
                      messagePreviewEmployees={employeeRecipients}
                      messagePreviewType="survey"
                      disabled={moment(endAt).isBefore(moment())}
                      buttonText={buttonText}
                      buttonClass={surveyId ? 'u-float-right' : undefined}
                      onSendMessage={handleSendMessage}
                      canPersonalize={canPersonalize}
                      // because we don't let you personalize when the survey is anonymous,
                      // we want to override the check to display the preview to show our added text
                      alwaysDisplayPreview={isAnonymous}
                      customizePreviewMessage={addDisclaimerMessage}
                      onChange={setLanguageToMessage}
                      onError={handleError}
                      isOnMobile={isOnMobile}
                      canTranslate
                    />
                  </FeatureFlagged>
                  {surveyType === SURVEY_TYPES.STANDARD && !isAnonymous ? (
                    <EmployeeMessageTemplates
                      orgId={orgId}
                      onSelect={handleTemplateChange}
                      templateType={TEMPLATE_TYPE.SURVEY}
                      messageType={MESSAGE_TYPE.TEXT}
                    />
                  ) : null}
                </>
              )}
            </div>
          </UiDialog.Content>
        </UiDialog>
      );
    },
  ),
);
